import cloneDeep from "lodash.clonedeep";
import { DatagridColumn, DatagridFilter } from "./types";

/**
 * Splits a filter search box value into the individual values to match on,
 * so lists can be pasted straight from a spreadsheet or an analytics table.
 *
 * Comma wins when present: it is the only separator that can carry values
 * containing spaces (creative names, publisher names), so a comma list keeps
 * matching whole phrases instead of being shredded into loose words.
 */
export const parseSearchTerms = (searchTerm: string): string[] =>
  searchTerm
    .split(searchTerm.includes(",") ? /,+/ : /\s+/)
    .map((term) => term.trim())
    .filter((term) => term.length > 0);

export const filterOptionsBySearch = (
  options: DatagridFilter[],
  searchTerm: string
): DatagridFilter[] => {
  const searchTerms = parseSearchTerms(searchTerm).map((term) =>
    term.toLowerCase()
  );

  // A value of only separators carries no terms and means "no search"; without
  // this an empty term would make includes("") match every option.
  if (searchTerms.length === 0) return options;

  return options.filter((option) => {
    const optionValue = option.value.toLowerCase();
    return searchTerms.some((term) => optionValue.includes(term));
  });
};

export const hasAnyValue = <T>(rows: T[], field: keyof T): boolean =>
  rows.some((row) => {
    const value = row[field];
    if (typeof value === "string") return value.trim() !== "";
    return value !== null && value !== undefined;
  });

export const getVisibleRows = (
  innerTrHeights: number[],
  scrollPosition: number,
  tableHeight: number
) => {
  const threshold = 3;

  let acum = 0;
  let indexStart = -1;
  let indexEnd = -1;

  let marginTop = 0;
  let marginBottom = 0;

  for (let i = 0; i < innerTrHeights.length; i++) {
    acum += innerTrHeights[i];

    if (acum > scrollPosition && indexStart === -1) {
      indexStart = i;
    }

    if (acum > scrollPosition + tableHeight) {
      indexEnd = i;
      break;
    }
  }

  if (indexEnd === -1 || indexStart === -1) {
    indexEnd = innerTrHeights.length - 1;
    indexStart = indexEnd;

    // This is a fix for a bug when at bottom scroll
    let a = 0;

    for (let i = innerTrHeights.length - 1; i >= 0; i--) {
      if (a > tableHeight) break;

      a += innerTrHeights[i];
      indexStart--;
    }
  }

  // At this point we have the index
  const visible: any = [];
  let filling: "top" | "bottom" = "top";

  for (let i = 0; i < innerTrHeights.length; i++) {
    let pushing = false;

    if (i >= indexStart - threshold && i <= indexEnd + threshold) {
      pushing = true;
    }

    visible.push(pushing);

    if (pushing) {
      filling = "bottom";
    } else {
      if (filling === "top") {
        marginTop += innerTrHeights[i];
      } else {
        marginBottom += innerTrHeights[i];
      }
    }
  }

  return {
    marginTop,
    marginBottom,
    visible,
  };
};

export function filterRows({
  data,
  columns,
  columnFilters,
  onSearch,
  globalFilter,
  columnMap,
}: {
  data: any[];
  columns: DatagridColumn[];
  columnFilters: { [field: string]: string[] | string };
  onSearch: (value: string, row: any) => boolean;
  globalFilter: string;
  columnMap: Map<string, DatagridColumn>;
}) {
  const filteredData = data
    .filter((row) => {
      return columns.every((column) => {
        if (!column.filterable) return true;
        const filterValue = columnFilters[column.field];

        // Handle multi-tab dropdown filters with filterGroups
        if (
          column.filterGroups &&
          typeof filterValue === "object" &&
          !Array.isArray(filterValue)
        ) {
          const groupFilters = filterValue as { [field: string]: string[] };
          const hasAnyFilter = Object.values(groupFilters).some(
            (values) => values && values.length > 0
          );

          if (!hasAnyFilter) return true;

          return column.filterGroups.some((group) => {
            const groupValues = groupFilters[group.field] || [];
            if (groupValues.length === 0) return false;

            const searchFn = group.onSearch || onSearch;
            return groupValues.some((value) => searchFn(value, row));
          });
        }

        // Handle dropdown filters with array values
        if (column.filterType === "dropdown" && Array.isArray(filterValue)) {
          if (filterValue.length === 0) return true;
          const searchFn = column.onSearch || onSearch;
          return filterValue.some((value) => searchFn(value, row));
        }

        // Handle text filters with string values
        const searchFn = column.onSearch || onSearch;
        return searchFn((filterValue as string) || "", row);
      });
    })
    .filter((row) => onSearch(globalFilter, row));

  const modifyingFilters = Object.keys(columnFilters).filter((field) => {
    const filterValue = columnFilters[field];
    const column = columnMap.get(field);
    const hasFilterValue =
      filterValue &&
      (typeof filterValue === "string" || Array.isArray(filterValue)) &&
      filterValue.length > 0;
    return hasFilterValue && column?.onSearchModify !== undefined;
  });

  if (modifyingFilters.length > 0) {
    return filteredData.map((row) => {
      let modifiedRow = cloneDeep(row);
      for (const field of modifyingFilters) {
        const column = columnMap.get(field);
        if (column?.onSearchModify) {
          modifiedRow =
            column.onSearchModify(columnFilters[field], modifiedRow) ??
            modifiedRow;
        }
      }
      return modifiedRow;
    });
  }

  return filteredData;
}

export function sortRows({
  rows,
  orderBy,
  order,
  allColumns,
}: {
  rows: any[];
  orderBy: string;
  order: "asc" | "desc";
  allColumns: DatagridColumn[];
}): any[] {
  return rows.sort((a: any, b: any) => {
    if (!orderBy) return 0;

    const defaultSort = (a: any, b: any) => {
      if (a[orderBy] < b[orderBy]) return -1;
      if (a[orderBy] > b[orderBy]) return 1;
      return 0;
    };
    const column = allColumns.find((column) => column.field === orderBy);
    const sortFn = column?.sortFn ?? defaultSort;

    const result = sortFn(a, b, orderBy);

    return order === "asc" ? result : -result;
  });
}
